import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save, ExternalLink, RotateCcw, FileText, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../api/client';

const SECTION_SCHEMA_HINT = `Sections is a JSON array. Each item has "kind" + fields:

• { "kind": "text", "heading": "...", "body": "Long paragraph..." }

• { "kind": "grid", "heading": "...", "items": [
    { "icon": "Truck", "title": "...", "body": "...", "meta": "optional" }
  ] }

• { "kind": "steps", "heading": "...", "items": [
    { "title": "Step 1", "body": "..." }
  ] }

• { "kind": "faq", "heading": "...", "items": [
    { "q": "Question?", "a": "Answer." }
  ] }

Icons: Phone, Mail, MapPin, Building2, Briefcase, Truck, ShieldCheck, Bell,
PackageOpen, AlertCircle, Wrench, Hammer, Users, Leaf, Wallet, Percent, FileText, Star.

cta = { "primary": { "label": "...", "href": "/route or mailto:" }, "secondary": {...} }`;

const StaticPagesAdmin = () => {
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState('');
  const [form, setForm] = useState(null);
  const [sectionsRaw, setSectionsRaw] = useState('');
  const [ctaRaw, setCtaRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await api.get('/pages');
      setPages(r.data);
      if (!selected && r.data.length) selectPage(r.data[0]);
    } catch (e) {
      toast.error('Failed to load pages');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  const selectPage = (p) => {
    setSelected(p.slug);
    setForm({
      slug: p.slug,
      title: p.title || '',
      subtitle: p.subtitle || '',
      breadcrumb: p.breadcrumb || '',
    });
    setSectionsRaw(JSON.stringify(p.sections || [], null, 2));
    setCtaRaw(p.cta ? JSON.stringify(p.cta, null, 2) : '');
    setError('');
  };

  const save = async () => {
    setError('');
    let sections;
    let cta = null;
    try {
      sections = JSON.parse(sectionsRaw || '[]');
      if (!Array.isArray(sections)) throw new Error('sections must be an array');
    } catch (e) {
      setError(`Sections JSON is invalid: ${e.message}`); return;
    }
    if (ctaRaw.trim()) {
      try { cta = JSON.parse(ctaRaw); } catch (e) {
        setError(`CTA JSON is invalid: ${e.message}`); return;
      }
    }
    setSaving(true);
    try {
      const r = await api.put(`/admin/pages/${selected}`, {
        title: form.title,
        subtitle: form.subtitle,
        breadcrumb: form.breadcrumb,
        sections,
        cta,
      });
      toast.success('Page saved');
      // refresh list + re-select
      const list = await api.get('/pages');
      setPages(list.data);
      selectPage(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const revertToServer = () => {
    const p = pages.find(x => x.slug === selected);
    if (p) selectPage(p);
  };

  if (loading || !form) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F46A25]" /></div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="static-pages-admin">
      {/* Sidebar */}
      <aside className="bg-white border border-neutral-200 rounded-sm p-2 h-fit">
        <div className="text-[11px] uppercase font-bold text-neutral-500 px-2 py-1">Footer pages ({pages.length})</div>
        <div className="max-h-[70vh] overflow-y-auto">
          {pages.map(p => (
            <button
              key={p.slug}
              onClick={() => selectPage(p)}
              data-testid={`admin-page-${p.slug}`}
              className={`w-full text-left px-2 py-1.5 text-sm rounded-sm flex items-center justify-between ${selected === p.slug ? 'bg-orange-50 text-[#F46A25] font-bold' : 'hover:bg-neutral-100 text-[#12263A]'}`}
            >
              <span className="flex items-center gap-1 truncate"><FileText className="w-3 h-3 shrink-0" /> {p.title || p.slug}</span>
              <ChevronRight className="w-3 h-3 shrink-0 opacity-40" />
            </button>
          ))}
        </div>
      </aside>

      {/* Editor */}
      <div className="bg-white border border-neutral-200 rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-[11px] uppercase font-bold text-neutral-500">Editing</div>
            <h3 className="text-lg font-black text-[#12263A]">{form.title || form.slug} <span className="text-xs font-mono text-neutral-500 ml-1">/pages/{form.slug}</span></h3>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/pages/${form.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs bg-white border border-neutral-300 hover:border-[#F46A25] font-bold px-3 py-1.5 rounded-sm flex items-center gap-1"
            ><ExternalLink className="w-3 h-3" /> Preview</Link>
            <button
              onClick={revertToServer}
              className="text-xs bg-white border border-neutral-300 hover:border-[#F46A25] font-bold px-3 py-1.5 rounded-sm flex items-center gap-1"
            ><RotateCcw className="w-3 h-3" /> Discard changes</button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-page-save"
              className="text-xs bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save page
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-[#12263A] uppercase mb-1">Title</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#12263A] uppercase mb-1">Breadcrumb group</label>
            <input value={form.breadcrumb} onChange={e => setForm(f => ({ ...f, breadcrumb: e.target.value }))} placeholder="e.g. Help & advice" className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#12263A] uppercase mb-1">Slug (URL)</label>
            <input value={form.slug} readOnly className="w-full border border-neutral-200 bg-neutral-100 rounded-sm px-2 py-2 text-sm font-mono text-neutral-500" />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-[#12263A] uppercase mb-1">Subtitle</label>
          <textarea value={form.subtitle} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} rows={2} className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#F46A25]" />
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-[#12263A] uppercase mb-1">Sections (JSON array)</label>
          <textarea
            value={sectionsRaw}
            onChange={e => setSectionsRaw(e.target.value)}
            rows={18}
            data-testid="admin-page-sections"
            className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-xs font-mono focus:outline-none focus:border-[#F46A25]"
          />
          <details className="mt-1">
            <summary className="text-[11px] text-[#F46A25] font-bold cursor-pointer">Schema help</summary>
            <pre className="text-[10px] bg-neutral-50 border border-neutral-200 rounded-sm p-2 mt-1 whitespace-pre-wrap leading-tight">{SECTION_SCHEMA_HINT}</pre>
          </details>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-[#12263A] uppercase mb-1">Call-to-action (JSON, optional)</label>
          <textarea
            value={ctaRaw}
            onChange={e => setCtaRaw(e.target.value)}
            rows={4}
            placeholder='{ "primary": {"label":"Contact us","href":"mailto:orders@drtoolsuk.com"} }'
            className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-xs font-mono focus:outline-none focus:border-[#F46A25]"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded-sm" data-testid="admin-page-error">{error}</div>
        )}

        <div className="text-[11px] text-neutral-500">
          Tip: hero (title + subtitle + breadcrumb), sections and the CTA box are all editable. Click <b>Preview</b> to open the live page in a new tab.
        </div>
      </div>
    </div>
  );
};

export default StaticPagesAdmin;
